import { FC, useMemo } from 'react';
import { useRecommendedCarCatalog } from './hook/useRecommendedCarCatalog';
import style from './recommendedCarCatalogStyle.module.css';
import carDefault from '@/assets/images/carImg.svg';
import cleaningIcon from '@/assets/images/recommendedCarCatalog/assurance_cleaning.png';
import vehicleCheckIcon from '@/assets/images/recommendedCarCatalog/assurance_maintenance.png';
import documentIcon from '@/assets/images/recommendedCarCatalog/assurance_process.png';
import reloadIcon from '@/assets/images/recommendedCarCatalog/assurance_return.png';
import warrantyIcon from '@/assets/images/recommendedCarCatalog/assurance_warranty.png';
import paymentStepImage from '@/assets/images/recommendedCarCatalog/payment_step3_figma.png';
import { PageTemplate } from '@/components/uiParts/Template/PageTemplate/PageTemplate';

interface RecommendedCarCatalogPageProps {
  mkCd?: string;
  carnmCd?: string;
}

const RECOMMENDED_CAR_INQUIRY_URL =
  'https://www.jicoo.com/t/EPUHG3-pVhTD/e/Re-vise';

export const RecommendedCarCatalogPage: FC<RecommendedCarCatalogPageProps> = ({
  mkCd,
  carnmCd,
}) => {
  const { data, isLoading, errorMessage } = useRecommendedCarCatalog({
    mkCd,
    carnmCd,
  });

  const imageUrl = useMemo(() => {
    if (!data?.image_url) {
      return carDefault;
    }

    return data.image_url;
  }, [data?.image_url]);

  const specRows = useMemo(() => {
    const sizeParts = [
      data?.catalog.size_length_mm
        ? `約${data.catalog.size_length_mm.toLocaleString()}mm`
        : null,
      data?.catalog.size_width_mm
        ? `約${data.catalog.size_width_mm.toLocaleString()}mm`
        : null,
      data?.catalog.size_height_mm
        ? `約${data.catalog.size_height_mm.toLocaleString()}mm`
        : null,
    ].filter(Boolean);

    return [
      {
        label: 'サイズ',
        value: sizeParts.length > 0 ? sizeParts.join(' × ') : '詳細確認中',
      },
      {
        label: '排気量',
        value: data?.catalog.displacement_cc
          ? `${data.catalog.displacement_cc.toLocaleString()}cc`
          : '詳細確認中',
      },
      { label: '駆動', value: data?.catalog.drive_type || '詳細確認中' },
      {
        label: '燃費',
        value: data?.catalog.fuel_economy
          ? `${data.catalog.fuel_economy}km/L`
          : '詳細確認中',
      },
    ];
  }, [data]);

  const assuranceItems = useMemo(
    () => [
      {
        icon: vehicleCheckIcon,
        title: '徹底した点検・整備',
        points: [
          '法定点検に加え、主要部位を中心とした多項目点検を実施',
          '消耗部品や安全に関わる箇所は基準に満たない場合は交換・整備',
          '点検内容・整備履歴はデータとして管理・確認可能',
        ],
      },
      {
        icon: cleaningIcon,
        title: 'クリーニング・品質管理',
        points: [
          '内装・外装を車両基準でクリーニング',
          'シート・内張り・エアコンなど、使用感が出やすい箇所も丁寧に対応',
          '次に乗る人が気持ちよく使える状態でお渡し',
        ],
      },
      {
        icon: warrantyIcon,
        title: '保証で購入後も安心',
        points: [
          '購入後も安心の保証付き',
          '走行に関わる主要部品を中心に保証対象',
          '万が一のトラブル時も、修理費用の不安を軽減',
        ],
      },
      {
        icon: reloadIcon,
        title: '返品・交換対応',
        points: [
          '購入後、一定期間内での返品・交換に対応',
          '実際に乗ってみて「合わなかった」を防ぐ仕組み',
          '中古車購入の不安をより小さく',
        ],
      },
      {
        icon: documentIcon,
        title: 'プロセスの透明性',
        points: [
          '車両状態・点検内容・保証範囲を事前にわかりやすく明示',
          '価格だけでなく、「なぜこの状態・この価格なのか」を説明',
          '納得して選べる中古車体験を提供',
        ],
      },
    ],
    [],
  );

  const purchaseSteps = useMemo(
    () => [
      {
        number: '1',
        title: '問い合わせ',
        description: '気になる車種を問い合わせ\n担当からお電話にてご連絡',
      },
      {
        number: '2',
        title: '注文',
        description: '購入したいクルマを選択して見積もり作成・注文',
      },
      {
        number: '3',
        title: '決済',
        description: '支払い方法に応じて必要な確認を進めます',
      },
      {
        number: '4',
        title: '書類準備',
        description:
          '初回注文が確定でき次第、販売会社より登録関連書類が届きますので14日以内にご返送ください',
        alert: '書類ご返送後2〜3週間',
      },
      {
        number: '5',
        title: '納車',
        description: '名義変更・納車店舗にてお引渡し',
      },
    ],
    [],
  );

  return (
    <PageTemplate
      pageName="recommended-car-catalog"
      title={data?.car_nm ?? 'おすすめ車種'}
      showNavBack
    >
      <div className={style.container}>
        {isLoading && <div className={style.loading}>読み込み中...</div>}

        {!isLoading && errorMessage && (
          <div className={style.errorBox}>{errorMessage}</div>
        )}

        {!isLoading && data && (
          <div className={style.contentCard}>
            <section className={style.topCard}>
              <div className={style.topHeader}>
                <p className={style.maker}>{data.maker_nm}</p>
                <h1 className={style.carName}>{data.car_nm}</h1>
              </div>
              <img
                src={imageUrl}
                alt={`${data.maker_nm} ${data.car_nm}`}
                className={style.heroImage}
                onError={({ currentTarget }) => {
                  currentTarget.src = carDefault;
                }}
              />
              <div className={style.specTable}>
                {specRows.map((row) => (
                  <div key={row.label} className={style.specRow}>
                    <div className={style.specRowLabel}>{row.label}</div>
                    <div className={style.specRowValue}>{row.value}</div>
                  </div>
                ))}
              </div>
              <p className={style.specCaption}>
                ※年式・グレードにより数値は異なります
              </p>
              <button
                type="button"
                className={style.primaryButton}
                onClick={() =>
                  window.open(RECOMMENDED_CAR_INQUIRY_URL, '_blank')
                }
              >
                {data.inquiry.label}
              </button>
            </section>

            <section className={style.assuranceCard}>
              <div className={style.assuranceHeader}>
                <p className={style.assuranceLead}>5つの安心</p>
              </div>

              <div className={style.assuranceList}>
                {assuranceItems.map((item) => (
                  <article key={item.title} className={style.assuranceItem}>
                    <div className={style.assuranceItemHeader}>
                      <img
                        src={item.icon}
                        alt=""
                        className={style.assuranceItemIcon}
                      />
                      <h2 className={style.assuranceItemTitle}>{item.title}</h2>
                    </div>
                    <ul className={style.assurancePoints}>
                      {item.points.map((point) => (
                        <li key={point}>{point}</li>
                      ))}
                    </ul>
                  </article>
                ))}
              </div>
            </section>

            <section className={style.flowSectionCard}>
              <p className={style.flowEyebrow}>最短1ヶ月で納車可能！</p>
              <h2 className={style.flowSectionTitle}>
                問い合わせから納車までの流れ
              </h2>

              <div className={style.timelineList}>
                {purchaseSteps.map((step) => (
                  <article key={step.number} className={style.timelineStep}>
                    <div className={style.timelineHeader}>
                      <span className={style.timelineNumber}>
                        {step.number}
                      </span>
                      <h3 className={style.timelineTitle}>{step.title}</h3>
                    </div>
                    {step.number !== '3' && (
                      <p className={style.timelineDescription}>
                        {step.description}
                      </p>
                    )}
                    {step.number === '3' && (
                      <img
                        src={paymentStepImage}
                        alt="決済方法の流れ"
                        className={style.paymentStepImage}
                      />
                    )}
                    {step.alert && (
                      <div className={style.timelineAlert}>{step.alert}</div>
                    )}
                  </article>
                ))}
              </div>
            </section>
          </div>
        )}
      </div>
    </PageTemplate>
  );
};
